import { useEffect, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, RefreshCw, Mail } from 'lucide-react';
import { fetchAdminProjects } from '../../lib/adminQueries';
import type { AdminProjectRow } from '../../types/admin';
import type { AdminUserEntry } from '../../lib/adminUserView';
import UserStatusBadges from './UserStatusBadges';
import AdminProjectViewer from './AdminProjectViewer';
import AdminEmailModal from './AdminEmailModal';

interface AdminUserDetailProps {
  user: AdminUserEntry;
  onBack: () => void;
  onPromote: (u: AdminUserEntry) => void;
  onDemote: (u: AdminUserEntry) => void;
  onLock: (u: AdminUserEntry) => void;
  onUnlock: (u: AdminUserEntry) => void;
  onDelete: (u: AdminUserEntry) => void;
  onReinstate: (u: AdminUserEntry) => void;
  onResetMfa: (u: AdminUserEntry) => void;
}

type ProjectSortKey = 'project_name' | 'status' | 'created_at' | 'completed_at' | 'pass_fail';

const PROJECT_COLUMNS: [ProjectSortKey, string][] = [
  ['project_name', 'Project'],
  ['status', 'Status'],
  ['created_at', 'Created'],
  ['completed_at', 'Completed'],
  ['pass_fail', 'Result'],
];

const PROJECT_DEFAULT_ASC: Record<ProjectSortKey, boolean> = {
  project_name: true,
  status: true,
  created_at: false,
  completed_at: false,
  pass_fail: true,
};

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

function formatDateTime(ts: string | null): string {
  if (!ts) return 'Never';
  return new Date(ts).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AdminUserDetail({
  user,
  onBack,
  onPromote,
  onDemote,
  onLock,
  onUnlock,
  onDelete,
  onReinstate,
  onResetMfa,
}: AdminUserDetailProps) {
  const [projects, setProjects] = useState<AdminProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<ProjectSortKey>('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [viewing, setViewing] = useState<AdminProjectRow | null>(null);
  const [emailing, setEmailing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setProjects(await fetchAdminProjects({ userId: user.id }));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (key: ProjectSortKey) => {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(PROJECT_DEFAULT_ASC[key]); }
  };

  const SortIcon = ({ k }: { k: ProjectSortKey }) =>
    sortKey === k
      ? (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
      : null;

  const sorted = [...projects].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  // Same precedence the actions menu used on the list page: platform admins are
  // exempt from account actions (the endpoint rejects them too).
  const status = user.role === 'admin'
    ? 'admin'
    : user.deleted_at ? 'deleted' : user.is_banned ? 'locked' : 'active';

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </button>

      {/* Identity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{user.full_name || '—'}</h2>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
          <UserStatusBadges user={user} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <MetaItem label="Joined" value={new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
          <MetaItem label="Last Activity" value={formatDateTime(user.last_active_at)} />
          <MetaItem label="Projects Created" value={String(user.projects_created)} />
          <MetaItem label="Projects Completed" value={String(user.projects_completed)} />
          <MetaItem label="Subscription" value={user.subscription_status ?? 'None (pay-per-use)'} />
          <MetaItem label="Two-Factor Auth" value={user.mfa_enrolled ? 'Enrolled' : 'Not enrolled'} />
          {user.firm_name && (
            <MetaItem
              label="Firm"
              value={`${user.firm_name}${user.firm_admin ? ' (firm admin)' : ''}${user.firm_admin && !user.has_seat ? ' (no seat)' : ''}`}
            />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Actions</p>
        <div className="flex flex-wrap gap-2">
          {status !== 'deleted' && (
            <button
              onClick={() => setEmailing(true)}
              data-testid="btn-send-email"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100"
            >
              <Mail className="w-4 h-4" />
              Send Email
            </button>
          )}
          {user.role === 'user' && status !== 'deleted' && (
            <button
              onClick={() => onPromote(user)}
              className="px-3 py-2 text-sm rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              Promote to Admin
            </button>
          )}
          {status === 'admin' && (
            <button
              onClick={() => onDemote(user)}
              className="px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100"
            >
              Remove Admin
            </button>
          )}
          {status === 'active' && (
            <button
              onClick={() => onLock(user)}
              className="px-3 py-2 text-sm rounded-md border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
            >
              Lock
            </button>
          )}
          {status === 'locked' && (
            <button
              onClick={() => onUnlock(user)}
              className="px-3 py-2 text-sm rounded-md border border-green-200 text-green-700 bg-green-50 hover:bg-green-100"
            >
              Unlock
            </button>
          )}
          {status === 'deleted' && (
            <button
              onClick={() => onReinstate(user)}
              className="px-3 py-2 text-sm rounded-md border border-green-200 text-green-700 bg-green-50 hover:bg-green-100"
            >
              Reinstate
            </button>
          )}
          {(status === 'active' || status === 'locked') && (
            <button
              onClick={() => onDelete(user)}
              className="px-3 py-2 text-sm rounded-md border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
            >
              Delete
            </button>
          )}
          {user.mfa_enrolled && status !== 'admin' && status !== 'deleted' && (
            <button
              onClick={() => onResetMfa(user)}
              data-testid="btn-reset-mfa"
              className="px-3 py-2 text-sm rounded-md border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
            >
              Reset MFA
            </button>
          )}
          {status === 'admin' && (
            <p className="text-xs text-gray-400 self-center">
              Lock and delete are disabled for admin accounts.
            </p>
          )}
        </div>
      </div>

      {/* Projects */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Projects</p>
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {error && (
          <div className="mx-6 my-4 bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading projects…</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {PROJECT_COLUMNS.map(([k, label]) => (
                      <th
                        key={k}
                        onClick={() => toggleSort(k)}
                        className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide text-xs cursor-pointer hover:text-gray-700 select-none"
                      >
                        <span className="flex items-center gap-1">{label}<SortIcon k={k} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">No projects</td>
                    </tr>
                  ) : sorted.map(p => (
                    <tr
                      key={p.id}
                      onClick={() => setViewing(p)}
                      className="hover:bg-blue-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{p.project_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          p.status === 'completed' ? 'bg-green-100 text-green-700'
                          : p.status === 'in-progress' ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {p.status === 'in-progress' ? 'In Progress' : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {p.completed_at ? new Date(p.completed_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {p.pass_fail ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            p.pass_fail === 'PASS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {p.pass_fail}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-xs text-gray-500">
              {sorted.length} project{sorted.length !== 1 ? 's' : ''}
            </div>
          </>
        )}
      </div>

      {viewing && (
        <AdminProjectViewer
          projectId={viewing.id}
          projectName={viewing.project_name}
          onClose={() => setViewing(null)}
        />
      )}

      {emailing && (
        <AdminEmailModal
          userId={user.id}
          userEmail={user.email}
          userName={user.full_name}
          onClose={() => setEmailing(false)}
        />
      )}
    </div>
  );
}
